import type { FC } from 'react';
import React, { useContext } from 'react';
import { RicosContext, RicosViewerContext } from 'ricos-context';
import type { NodeViewRenderer, NodeViewRendererProps } from 'ricos-types';

import type { ImagePluginViewerConfig } from '../types';
import { IMAGE_TYPE } from '../types';
import { alignmentClassName, sizeClassName, textWrapClassName } from './class-name-strategies';
import { getExpandFunction } from './get-expand-function';
import { getImageClickActionConfig } from './get-image-click-action-config';
import { Image } from './image';
import { ViewerImageCaption } from './viewer-image-caption';

const ViewerImage: FC<NodeViewRendererProps> = ({ node, nodeIndex, NodeViewContainer }) => {
  const { config: viewerConfig, seoMode, helpers } = useContext(RicosViewerContext);
  const { isMobile, t } = useContext(RicosContext);
  const config = (viewerConfig[IMAGE_TYPE] ?? {}) as ImagePluginViewerConfig;
  const expand = getExpandFunction({
    helpers,
    config,
    nodeId: node.id,
    imageData: node.imageData,
  });

  const imageClickActionConfig = getImageClickActionConfig({
    imageData: node.imageData,
    config,
    expand,
  });

  return (
    <NodeViewContainer
      node={node}
      nodeIndex={nodeIndex}
      customClassNames={{
        alignment: alignmentClassName(node),
        size: sizeClassName(node, isMobile),
        textWrap: textWrapClassName(node),
      }}
    >
      <Image
        node={node}
        imageClickActionConfig={imageClickActionConfig}
        expand={expand}
        config={config}
        seoMode={seoMode}
        helpers={helpers}
        isMobile={isMobile}
        shouldLazyLoadImages
        captionComponent={
          <ViewerImageCaption
            imageType={imageClickActionConfig.type}
            caption={node?.imageData?.caption}
            defaultCaption={t('ImageViewer_Caption')}
          />
        }
      />
    </NodeViewContainer>
  );
};

const ImageRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <ViewerImage key={node.id} node={node} {...props} />;
};

export default ImageRenderer;
